import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Badge — cinco temas semânticos + neutro + "aguardando fonte".
 *
 * A anatomia é a do sustenta-ai-app: fundo em *wash* da própria cor, borda a
 * 40% e texto na cor cheia. Contorno puro sobre branco (o que as classes
 * `chip-*` do app original faziam) fica leve demais numa tabela densa e some
 * no meio das linhas; o wash dá peso sem virar bloco chapado.
 *
 * Hard rule 2: estado nunca é só cor. O `icon` existe para que todo badge
 * semântico carregue glifo além do rótulo — ver `patterns/status-badge.tsx`.
 */
const badgeVariants = cva(
  'inline-flex items-center gap-1.5 whitespace-nowrap rounded-chip border px-2 py-0.5 text-[0.688rem] font-bold leading-4 [&_svg]:size-3 [&_svg]:shrink-0',
  {
    variants: {
      tone: {
        neutral: 'border-border bg-bg-subtle text-on-surface-muted',
        primary: 'border-primary-border bg-primary-wash text-primary',
        success: 'border-success-border bg-success-wash text-success',
        warning: 'border-warning-border bg-warning-wash text-warning-text',
        danger: 'border-danger-border bg-danger-wash text-danger',
        info: 'border-info-border bg-info-wash text-info',
        /** Sem integração ainda: cinza tracejado, nunca zero. */
        pending: 'border-dashed border-border-strong bg-bg-subtle text-on-surface-muted',
      },
      /** Reservado para o estado terminal de um fluxo (ex.: "Concluído"). */
      filled: { true: 'text-on-primary', false: '' },
    },
    compoundVariants: [
      { tone: 'success', filled: true, class: 'border-success bg-success' },
      { tone: 'danger', filled: true, class: 'border-danger bg-danger' },
      { tone: 'primary', filled: true, class: 'border-primary bg-primary' },
      { tone: 'info', filled: true, class: 'border-info bg-info' },
      { tone: 'warning', filled: true, class: 'border-warning-border bg-warning-wash text-warning-text' },
    ],
    defaultVariants: { tone: 'neutral', filled: false },
  },
);

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {
  icon?: React.ReactNode;
}

export function Badge({ className, tone, filled, icon, children, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ tone, filled }), className)} {...props}>
      {icon}
      {children}
    </span>
  );
}
